import { useAppDispatch } from '@/hooks/useReduxHooks';
import { updateCs } from '@/store/modules/cs';
import { Chat, TextRequest, TextResponse } from '@/types/cs.type';
import { format, subDays } from 'date-fns';
import ICON_KTP_USER from '@/assets/cs/icon_ktp_user.png';
import ICON_KTP_USER_DISABLED from '@/assets/cs/icon_ktp_user_disabled.png';
import ICON_KTP_ADMIN from '@/assets/cs/Icon_ktp_admin.png';
import styled, { css } from 'styled-components';
import useGetCsMessage from '@/hooks/useGetCsMessageAlert';
import { useMemo } from 'react';

type Props = {
  info: Chat;
};

function CsChatItem({ info }: Props) {
  const dispatch = useAppDispatch();
  const { messageAlert } = useGetCsMessage();

  const onOpenChat = ({ chatId }: { chatId: number }) => {
    dispatch(
      updateCs({
        isOpenChat: true,
        chatId,
        chatType:
          info.chatType === 'ONE_ON_ONE'
            ? 'CHAT'
            : info.chatType === 'PACKAGE'
            ? 'PACKAGE'
            : info.chatType === 'ADMIN_NOTICE'
            ? 'ADMIN_NOTICE'
            : 'REFUND',
      }),
    );
  };

  const onClickChat = ({
    chatId,
    notificationType,
  }: {
    chatId: number;
    notificationType: string;
  }) => {
    if (
      notificationType === 'ONE_ON_ONE' ||
      notificationType === 'REQUIREMENT_RESULT'
    ) {
      onOpenChat({ chatId });
    } else {
      onOpenChat({ chatId });
    }
  };

  const isAdminChat = () => {
    return info.chatType === 'ADMIN_NOTICE';
  };

  const getTitle = (dateTime: string) => {
    if (isAdminChat()) {
      return 'KTP 고객지원팀';
    }

    if (
      format(new Date(dateTime), 'yyyy-MM-dd') ===
      format(new Date(), 'yyyy-MM-dd')
    ) {
      return format(new Date(dateTime), '오늘 HH:mm');
    }

    return format(new Date(dateTime), 'yyyy-MM-dd HH:mm');
  };

  const getTime = (dateTime: string) => {
    if (
      format(new Date(dateTime), 'yyyy-MM-dd') ===
      format(new Date(), 'yyyy-MM-dd')
    ) {
      return format(new Date(dateTime), 'HH:mm');
    }

    if (
      format(new Date(dateTime), 'yyyy-MM-dd') ===
      format(subDays(new Date(), 1), 'yyyy-MM-dd')
    ) {
      return '어제';
    }

    return format(new Date(dateTime), 'M월 d일');
  };

  const isNew = useMemo(() => {
    const newAlertCount = messageAlert?.notifications.find(
      (notification) => notification.chatId === info.id,
    )?.has1on1;

    if (newAlertCount) {
      return newAlertCount > 0;
    }

    return false;
  }, [messageAlert, info]);

  const previewRenderMap: Record<string, any> = {
    TEXT_REQUEST: (info.messages[info.messages.length - 1] as TextRequest)
      .content,
    APPEND_TEXT_REQUEST: (
      info.messages[info.messages.length - 1] as TextRequest
    ).content,
    ADMIN_TEXT_REQUEST: (info.messages[info.messages.length - 1] as TextRequest)
      .content,
    TEXT_RESPONSE: (info.messages[info.messages.length - 1] as TextResponse)
      .content,
    REFUND_CREATE_REQUEST: '정상적으로 요청되었습니다',
    REFUND_CREATE_CONFIRMED: '환급건이 정상적으로 등록되었습니다',
    REFUND_CREATE_REJECTED: '환급건이 반려되었습니다',
    PACKAGE_REQUEST: '정상적으로 신청되었습니다',
  };

  const isGrayTheme =
    info.lastMessageType === 'PACKAGE_REQUEST' ? !isNew : !!info.deletedAt;

  const getImage = () => {
    if (info.lastMessageType === 'PACKAGE_REQUEST' && !isNew) {
      return ICON_KTP_USER_DISABLED;
    }
    if (!!info.deletedAt) {
      return ICON_KTP_USER_DISABLED;
    }
    if (isAdminChat()) {
      return ICON_KTP_ADMIN;
    }
    return ICON_KTP_USER;
  };

  return (
    <ChatItem
      onClick={() =>
        onClickChat({
          chatId: info.id,
          notificationType: info.chatType,
        })
      }
    >
      <ChatHeader>
        <img src={getImage()} alt='' width={24} />
        {!isAdminChat() && <ChatTag end={isGrayTheme}>대화시작</ChatTag>}
        <TitleWrapper>
          <ChatTitle end={isGrayTheme}>{getTitle(info.createdDate)}</ChatTitle>
          {isNew && <NewLabel />}
        </TitleWrapper>
        <ChatTime end={isGrayTheme}>
          {!!info.deletedAt ? '종료됨' : getTime(info.lastMessageDate)}
        </ChatTime>
      </ChatHeader>
      <ChatContent end={isGrayTheme}>
        {previewRenderMap[info.lastMessageType]}
      </ChatContent>
    </ChatItem>
  );
}

const ChatItem = styled.button`
  margin: 0 16px 0 16px;
  padding: 13px 0 20px 0;

  &:not(:last-child) {
    border-bottom: 1px solid #e5e6e8;
  }
`;

const ChatHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 6px;
  margin-bottom: 6px;
`;

const ChatTag = styled.span<{ end: boolean }>`
  padding: 1px 6px;
  color: ${({ end }) => (end ? '#7CA7FA' : '#5089f8')};
  font-size: 12px;
  font-weight: 500;
  line-height: 150%;
  letter-spacing: -0.36px;
  background-color: #f4f8ff;
  border-radius: 2px;
  flex-shrink: 0;
`;

const ChatTitle = styled.strong<{ end: boolean }>`
  position: relative;
  color: ${({ end }) => (end ? '#A7A7A7' : '#000')};
  font-size: 16px;
  font-weight: 600;
  line-height: 150%;
  letter-spacing: -0.48px;
  max-width: 132px;
  overflow: hidden;
  text-overflow: clip;
  white-space: nowrap;
`;

const ChatTime = styled.span<{ end: boolean }>`
  margin-left: auto;
  color: ${({ end }) => (end ? '#A7A7A7' : '#5f6165')};
  font-size: 14px;
  font-weight: 400;
  line-height: 150%;
  letter-spacing: -0.28px;
  flex-shrink: 0;
`;

const ChatContent = styled.p<{ end: boolean }>`
  margin-left: 32px;
  height: 44px;
  text-overflow: ellipsis;
  overflow: hidden;
  word-break: break-word;
  display: -webkit-box;
  -webkit-line-clamp: 2; // 원하는 라인수
  -webkit-box-orient: vertical;
  color: ${({ end }) => (end ? '#80848A' : '#3a3b3e')};
  font-size: 14px;
  font-weight: 400;
  line-height: 150%;
  letter-spacing: -0.28px;
  text-align: left;
`;

const TitleWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 4px;
`;

const NewLabel = styled.div`
  width: 5px;
  height: 5px;
  background-color: #ff2d55;
  border-radius: 100px;
  margin-top: 2px;
`;

export default CsChatItem;
